// ErrorState — soft red banner panel for "we couldn't load this" surfaces.
// Pattern from 00-design-system.html §08: amber/red icon, headline, body,
// optional retry button.
//
// Use for network/data fetch failures inside a module. For form validation
// errors, prefer inline field errors on the input.

import type { ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

export interface ErrorStateProps {
  title?: string;
  body?: string;
  onRetry?: () => void;
  retryLabel?: string;
  /** Pass custom action(s) instead of the default Retry button. */
  actions?: ReactNode;
  className?: string;
}

export function ErrorState({
  title = "Couldn't load",
  body,
  onRetry,
  retryLabel = 'Retry now',
  actions,
  className,
}: ErrorStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center rounded-xl border border-st-danger-border bg-st-danger-bg/40 p-8 text-center ${className ?? ''}`}
    >
      <AlertTriangle className="h-12 w-12 text-st-danger-solid" strokeWidth={1.5} />
      <div className="mt-4 text-lg font-medium text-tcs-text">{title}</div>
      {body && (
        <div className="mt-1 max-w-md text-sm text-tcs-text-secondary">
          {body}
        </div>
      )}
      {(actions || onRetry) && (
        <div className="mt-5">
          {actions ?? (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-tcs-inverse px-4 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover"
            >
              <RefreshCw className="h-4 w-4" />
              {retryLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
